<template>
  <div class="article__wrapper">
    <h1 class="article__title title">{{ title }}</h1>

    <a-lazy-hydrate
      when-visible
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.GENERAL_INFO
        )
      "
    >
      <div class="article__publisher-details">
        <a-article-info
          :category="category"
          :author="author"
          :published-on="publishedOn"
          :sponsorship-company="sponsorshipCompany"
          @mounted="
            $_lazyHydratedComponentState_onHydratedComponentMounted(
              $options.consts.HYDRATED_SECTION_NAME.GENERAL_INFO
            )
          "
        />
      </div>
    </a-lazy-hydrate>

    <a-lazy-hydrate
      when-visible
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.TLDR
        )
      "
    >
      <a-tldr
        :tldr="tldr"
        :font-bold="false"
        class="article__tldr"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.TLDR
          )
        "
      />
    </a-lazy-hydrate>

    <div v-if="image || video" class="article__image-wrapper">
      <a-lazy-hydrate
        when-visible
        :key="
          $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
            $options.consts.HYDRATED_SECTION_NAME.MEDIA
          )
        "
      >
        <a-media
          :image="image"
          :image-aspect-ratio="featuredMediaAspectRatio"
          show-caption
          enlargeable-image
          viewport-optimization
          :srcset-location="srcsetLocation"
          :video="video"
          :video-aspect-ratio="featuredMediaAspectRatio"
          class="article__featured-media"
          @click-on-image="openFeaturedImage"
          @mounted="
            $_lazyHydratedComponentState_onHydratedComponentMounted(
              $options.consts.HYDRATED_SECTION_NAME.MEDIA
            )
          "
        />
      </a-lazy-hydrate>
    </div>

    <transition>
      <article-body
        :body="body"
        :ebook="ebook"
        :terms="terms"
        :is-sponsored="isSponsored"
        :table-data="tableData"
        :is-migrated="isMigrated"
        :image="image"
        :article-text-size="this.articleTextSize"
        class="article__content-body"
        @set-popup-image="setPopupImage"
      />
    </transition>

    <!--
      Do not remove the "key", it avoids the DOM mismatch if content
      is refreshed
    -->
    <a-lazy-hydrate
      v-if="$helper.isNotAmpPage($route.name)"
      when-visible
      key="article-banner-1"
    >
      <a-visibility :show="true" :on="[$breakpoint.mobile]">
        <a-banner
          class="article__banner"
          :wrapped-in-card="true"
          :banner-settings="$options.consts.BANNER_SETTINGS.ARTICLE.MPU_1"
        />
      </a-visibility>
    </a-lazy-hydrate>

    <i
      v-if="$helper.isNotAmpPage($route.name)"
      class="article__horizontal-delimiter"
    />

    <a-lazy-hydrate
      v-if="$helper.isNotAmpPage($route.name)"
      when-visible
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.TAGS
        )
      "
    >
      <div class="article__text-tag-icons-container">
        <div class="article__text-tag-container">
          <a-text-size-button
            class="article__text-size"
            @select="handleSizeChange"
          />
          <tag-list
            :tags="tags"
            class="article__tag"
            @mounted="
              $_lazyHydratedComponentState_onHydratedComponentMounted(
                $options.consts.HYDRATED_SECTION_NAME.TAGS
              )
            "
          />
        </div>

        <a-action-icon-list
          :action-icons="actionIcons"
          v-on="$listeners"
          class="article__action-icons"
        />
      </div>
    </a-lazy-hydrate>

    <div
      v-if="$helper.isAmpPage($route.name)"
      class="article__text-tag-icons-container"
    >
      <div class="article__text-tag-container">
        <tag-list :tags="tags" class="article__tag" />
      </div>

      <a-action-icon-list
        :action-icons="actionIcons"
        v-on="$listeners"
        class="article__action-icons"
      />
    </div>

    <a-lazy-hydrate
      when-visible
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.MOST_POPULAR_LIST
        )
      "
    >
      <most-popular-list
        :items="mostCommentedArticles"
        @mounted="
          $_lazyHydratedComponentState_onHydratedComponentMounted(
            $options.consts.HYDRATED_SECTION_NAME.MOST_POPULAR_LIST
          )
        "
      />
    </a-lazy-hydrate>

    <a-lazy-hydrate when-visible>
      <a-visibility :show="true" :on="[$breakpoint.mobile]">
        <a-banner
          class="article__start-trading banner-card--black"
          :banner-settings="
            $options.consts.BANNER_SETTINGS.ARTICLE.START_TRADING
          "
        />
      </a-visibility>
    </a-lazy-hydrate>

    <a-visibility show :on="[$breakpoint.mobile]">
      <a-top-brokers-with-delayed-refresh class="article__top-brokers" />
    </a-visibility>

    <template v-if="$helper.isNotAmpPage($route.name)">
      <a-lazy-hydrate when-visible>
        <a-visibility :show="true" :on="[$breakpoint.mobile]">
          <a-banner
            class="article__banner"
            :wrapped-in-card="true"
            :banner-settings="$options.consts.BANNER_SETTINGS.ARTICLE.MPU_2"
          />
        </a-visibility>
      </a-lazy-hydrate>
    </template>

    <template v-if="$helper.isNotAmpPage($route.name)">
      <a-lazy-hydrate when-visible>
        <a-visibility :show="true" :on="[$breakpoint.mobile]">
          <a-banner
            class="article__banner"
            :wrapped-in-card="true"
            :banner-settings="$options.consts.BANNER_SETTINGS.ARTICLE.MPU_3"
          />
        </a-visibility>
      </a-lazy-hydrate>
    </template>

    <template v-if="$helper.isNotAmpPage($route.name)">
      <a-image-download-modal v-if="popupImage" :image="popupImage" />
    </template>
  </div>
</template>

<script>
import mixins from '@/utils/mixins'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import AArticleInfo from 'shared/AArticleInfo'
import ATldr from 'shared/ATldr'
import ATextSizeButton from 'shared/ATextSizeButton'
import {
  ARTICLE_TEXT_SIZE,
  ACCESSIBILITY_TEXT_SIZE_STORAGE_KEY
} from 'shared/ATextSizeButton/enums'
import AActionIconList from 'shared/AActionIconList'
import { ACTION_ICON } from 'shared/AActionIcon'
import ArticleBody from './ArticleBody'
import TagList from './TagList'
import MostPopularList from './MostPopularList'
import AImageDownloadModal from 'shared/AImageDownloadModal'
import { aspectRatios } from 'enums/aspectRatios'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { MODAL } from 'modals/AModalWrapper'
import AMedia from 'shared/AMedia'
import { DFP_BANNER } from 'enums/banners/dfp-banner'
import { SRCSET_LOCATION } from '@/utils/enums/images'
import { removeAmpSuffix } from '@fmpedia/helpers'
import { COOKIE_ENTITY_TYPE } from '~/utils/enums/oneTrust'
import ABanner from 'shared/ABanner/index.vue'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import ATopBrokersWithDelayedRefresh from '@/components/_shared/ATopBrokersWithDelayedRefresh/index.vue'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from '@/utils/enums/article'

const HYDRATED_SECTION_NAME = {
  GENERAL_INFO: 'GENERAL_INFO',
  TLDR: 'TLDR',
  MEDIA: 'MEDIA',
  BODY: 'BODY',
  TAGS: 'TAGS',
  MOST_POPULAR_LIST: 'MOST_POPULAR_LIST'
}

export default {
  name: 'AArticle',
  mixins: [
    mixins.initialScroll,
    mixins.urlFormatters,
    mixins.lazyHydratedComponentState
  ],
  components: {
    ATopBrokersWithDelayedRefresh,
    ABanner,
    AMedia,
    AArticleInfo,
    ATldr,
    ArticleBody,
    TagList,
    ATextSizeButton,
    AActionIconList,
    MostPopularList,
    AImageDownloadModal
  },
  props: {
    articleId: propValidator([PROP_TYPES.STRING]),
    articleSlug: propValidator([PROP_TYPES.STRING]),
    isSaved: propValidator([PROP_TYPES.BOOLEAN], false, false),
    isSessionWrap: propValidator([PROP_TYPES.BOOLEAN], false, false),
    isTradeCompass: propValidator([PROP_TYPES.BOOLEAN], false, false),
    category: propValidator([PROP_TYPES.OBJECT]),
    title: propValidator([PROP_TYPES.STRING]),
    tldr: propValidator([PROP_TYPES.STRING], false),
    author: propValidator([PROP_TYPES.OBJECT], false),
    sponsorshipCompany: propValidator([PROP_TYPES.OBJECT], false),
    publishedOn: propValidator([PROP_TYPES.STRING]),
    body: propValidator([PROP_TYPES.STRING]),
    ebook: propValidator([PROP_TYPES.OBJECT], false),
    tags: propValidator([PROP_TYPES.ARRAY]),
    image: propValidator([PROP_TYPES.OBJECT], false),
    video: propValidator([PROP_TYPES.OBJECT], false),
    terms: propValidator([PROP_TYPES.ARRAY]),
    tableData: propValidator([PROP_TYPES.STRING], false),
    isMigrated: propValidator([PROP_TYPES.BOOLEAN]),
    mostCommentedArticles: propValidator([PROP_TYPES.ARRAY], false, () => []),
    canonicalUrl: propValidator([PROP_TYPES.STRING])
  },
  consts: {
    BANNER_SETTINGS,
    HYDRATED_SECTION_NAME
  },
  data() {
    return {
      popupImage: null,
      STICKY_BANNER_POSITION,
      DFP_BANNER,
      articleTextSize: ARTICLE_TEXT_SIZE.NORMAL
    }
  },
  computed: {
    isSponsored() {
      return !!this.sponsorshipCompany
    },
    featuredImageAspectRatio() {
      if (this.isMigrated) return aspectRatios.noRatio

      return aspectRatios.articleFeaturedImage
    },
    featuredMediaAspectRatio() {
      if (this.image) return this.featuredImageAspectRatio

      return aspectRatios.articleFeaturedVideo
    },
    srcsetLocation() {
      return this.$helper.isAmpPage(this.$route.name)
        ? SRCSET_LOCATION.AMP_ARTICLE_PAGE
        : SRCSET_LOCATION.ARTICLE_PAGE
    },
    actionIcons() {
      const { fullPath: path } = this.$route

      return [
        {
          actionIcon: ACTION_ICON.SAVE,
          id: this.articleId,
          isSaved: this.isSaved,
          ...this.additionalIconProps,
          ariaLabel: 'Save'
        },
        {
          actionIcon: ACTION_ICON.TWITTER,
          title: this.title,
          path,
          ariaLabel: 'Twitter'
        },
        {
          actionIcon: ACTION_ICON.TELEGRAM,
          title: this.title,
          path,
          ariaLabel: 'Telegram'
        },
        {
          actionIcon: ACTION_ICON.FACEBOOK,
          title: this.title,
          path,
          ariaLabel: 'Facebook'
        }
      ]
    },
    additionalIconProps() {
      return this.$helper.isAmpPage(this.$route.name)
        ? { to: removeAmpSuffix(this.$route.path) }
        : {}
    }
  },
  methods: {
    setPopupImage(image) {
      this.popupImage = image
    },
    openFeaturedImage() {
      this.setPopupImage(this.image)
      this.$nextTick(() => {
        this.$helper.openModal(MODAL.IMAGE_DOWNLOAD)
      })
    },
    rerenderNonHydratedComponents() {
      this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
        Object.values(HYDRATED_SECTION_NAME)
      )
    },
    handleSizeChange(size) {
      this.articleTextSize = size
    },
    setSavedTextSize() {
      const savedSize = window.localStorage.getItem(
        ACCESSIBILITY_TEXT_SIZE_STORAGE_KEY
      )
      if (savedSize) {
        this.articleTextSize = savedSize
      }
    }
  },
  beforeMount() {
    this.$bus.$on(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.rerenderNonHydratedComponents
    )
  },
  async mounted() {
    this.setSavedTextSize()

    /** Send event only in case of:
     *  production && it is not article preview mode **/
    if (
      process.env.NODE_ENV === 'production' &&
      !this.$route.params.previewId &&
      this.$_oneTrust_isConsentGivenForEntity(
        COOKIE_ENTITY_TYPE.GOOGLE_ANALYTICS
      )
    ) {
      /* Timeout is placed here because in a few cases GA sent wrong path (from
       * previous page). On 'nextTick' same issue.
       */
      setTimeout(() => {
        window.dataLayer.push({
          event: 'Article view'
        })
      })
    }
  },
  beforeDestroy() {
    this.$bus.$off(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.rerenderNonHydratedComponents
    )
  }
}
</script>

<style lang="scss" scoped>
@import '~assets/scss/local/styles/_article-body.scss';

/* The following selector is replaced during the build process.
The value is generated randomly to avoid detection by AdBlockers.
The value here must be synced with the values in utils/enums/ad-blocker/element-placeholder.js
*/
$sponsored-article-indicator: 'article-sponsored-indicator';

.article__wrapper {
  .article__header {
    margin-bottom: 10px;

    @include mobile {
      padding-right: 50px; /* limit text to avoid overlap with JoinTelegram widget */
    }
  }

  .#{$sponsored-article-indicator} {
    margin-left: auto;
    color: $c--main;
    font-weight: 600;

    @include mobile {
      position: absolute;
      right: 0;
      font-size: 12px;
    }
  }

  .banner-wrapper {
    margin: 30px 0;
  }
}

.article__publisher-details {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 16px 0;
}

.article__tldr {
  margin-top: 16px;

  /deep/ li {
    @include subtitle4;
  }
}

$author-logo-height: 50px;
$company-logo-height: 70px;
$icon-height: 35px;

.article__image-wrapper {
  margin-top: 20px;
}

.article__featured-media {
  /deep/ .a-image__wrapper {
    border-radius: 6px;
    cursor: pointer;
  }
}

.article__content-body {
  margin-top: 32px;
}

.article__text-tag-icons-container {
  display: flex;
  justify-content: space-between;
  padding-bottom: 12px;
  margin-top: 24px;

  @include tablet {
    flex-direction: column;
  }
}

.article__text-tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  @include tablet {
    margin-bottom: 16px;
  }
}

.article__tag {
  display: flex;
  justify-content: center;

  @include tablet {
    justify-content: flex-start;
  }
}

.article__banner {
  margin: 24px 0;
}

.article__start-trading {
  margin-top: 24px;
}

.article__top-brokers {
  margin-top: 32px;
}

.article__horizontal-delimiter {
  display: block;
  width: 100%;
  height: 1px;
  margin-top: 40px;
  background: $c--gray-light;

  @include tablet {
    margin-top: 24px;
  }
}
</style>
